import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/**
 * Page chrome: what this screen is, WHEN the data on it was computed, and the
 * two facts an operator needs before reading a single row — that the screen is
 * live, and that it is read-only.
 *
 * `computedAt` is not decoration. A polled screen that does not say when it
 * last succeeded has exactly the defect this endpoint refuses to have: it
 * cannot be told apart from a stale cache.
 */
export const MoneyHealthHeader = ({
  computedAtLabel,
  refreshSeconds,
}: {
  computedAtLabel: string | null;
  refreshSeconds: number;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      <Box>
        <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.2px' }}>
          {t('moneyHealth.title')}
        </Typography>
        {computedAtLabel === null ? null : (
          <Typography
            data-testid="money-health-computed-at"
            sx={{ fontSize: 12, color: tokens.text.tertiary }}
          >
            {t('moneyHealth.subtitle', { at: computedAtLabel, seconds: refreshSeconds })}
          </Typography>
        )}
      </Box>
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="span"
          sx={{
            px: 2.25,
            py: 0.5,
            borderRadius: `${tokens.radius.pill}px`,
            fontSize: 11,
            fontWeight: 600,
            background: tokens.surface.muted,
            color: tokens.text.secondary,
          }}
        >
          {t('moneyHealth.readOnly')}
        </Box>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.25,
            py: 0.5,
            borderRadius: `${tokens.radius.pill}px`,
            fontSize: 11,
            fontWeight: 600,
            background: tokens.state.successBg,
            color: tokens.state.success,
          }}
        >
          <Box
            component="span"
            sx={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }}
          />
          {t('moneyHealth.live')}
        </Box>
      </Box>
    </Box>
  );
};
